import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { SurveyAnalyticsService } from "Services/axios/SurveyAnalytics.Service";
import { handleApiError } from "Features/Common/ErrorHandler";
import {
	normalizeAnalytics,
	normalizeRespondentDetail,
	normalizeRespondentsList,
} from "./surveyAnalyticsNormalizer";

export const fetchSurveyAnalyticsThunk = createAsyncThunk(
	"surveyAnalytics/fetchAnalytics",
	async (params = {}, { rejectWithValue }) => {
		try {
			const response = await SurveyAnalyticsService.getAnalytics(params);
			return normalizeAnalytics(response.data);
		} catch (error) {
			handleApiError(error);
			return rejectWithValue(
				error?.response?.data?.message ||
					error?.message ||
					"Gagal memuat data analytics survey"
			);
		}
	}
);

export const fetchSurveyRespondentsThunk = createAsyncThunk(
	"surveyAnalytics/fetchRespondents",
	async (params, { rejectWithValue }) => {
		try {
			const response = await SurveyAnalyticsService.getRespondents(params);
			return normalizeRespondentsList(response.data);
		} catch (error) {
			handleApiError(error);
			return rejectWithValue(
				error?.response?.data?.message ||
					error?.message ||
					"Gagal memuat daftar responden"
			);
		}
	}
);

export const fetchSurveyRespondentDetailThunk = createAsyncThunk(
	"surveyAnalytics/fetchRespondentDetail",
	async (id, { rejectWithValue }) => {
		try {
			const response = await SurveyAnalyticsService.getRespondentDetail(id);
			return normalizeRespondentDetail(response.data);
		} catch (error) {
			handleApiError(error);
			return rejectWithValue(
				error?.response?.data?.message ||
					error?.message ||
					"Gagal memuat detail responden"
			);
		}
	}
);

const surveyAnalyticsSlice = createSlice({
	name: "surveyAnalytics",
	initialState: {
		analytics: null,
		respondents: [],
		pagination: null,
		respondentDetail: null,
		filters: {
			page: 1,
			per_page: 15,
			start_date: null,
			end_date: null,
			preset: "all",
			year: new Date().getFullYear(),
		},
		isLoadingAnalytics: false,
		isLoadingRespondents: false,
		isLoadingDetail: false,
		error: null,
		detailError: null,
	},
	reducers: {
		setRespondentFilters: (state, action) => {
			state.filters = { ...state.filters, ...action.payload };
		},
		setDateFilters: (state, action) => {
			state.filters = {
				...state.filters,
				...action.payload,
				page: 1,
			};
		},
		clearRespondentDetail: (state) => {
			state.respondentDetail = null;
			state.detailError = null;
			state.isLoadingDetail = false;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchSurveyAnalyticsThunk.pending, (state) => {
				state.isLoadingAnalytics = true;
				state.error = null;
			})
			.addCase(fetchSurveyAnalyticsThunk.fulfilled, (state, action) => {
				state.isLoadingAnalytics = false;
				state.analytics = action.payload;
			})
			.addCase(fetchSurveyAnalyticsThunk.rejected, (state, action) => {
				state.isLoadingAnalytics = false;
				state.error = action.payload;
			})
			.addCase(fetchSurveyRespondentsThunk.pending, (state) => {
				state.isLoadingRespondents = true;
				state.error = null;
			})
			.addCase(fetchSurveyRespondentsThunk.fulfilled, (state, action) => {
				state.isLoadingRespondents = false;
				state.respondents = action.payload.data;
				state.pagination = action.payload.pagination;
			})
			.addCase(fetchSurveyRespondentsThunk.rejected, (state, action) => {
				state.isLoadingRespondents = false;
				state.error = action.payload;
			})
			.addCase(fetchSurveyRespondentDetailThunk.pending, (state) => {
				state.isLoadingDetail = true;
				state.detailError = null;
			})
			.addCase(fetchSurveyRespondentDetailThunk.fulfilled, (state, action) => {
				state.isLoadingDetail = false;
				state.respondentDetail = action.payload;
			})
			.addCase(fetchSurveyRespondentDetailThunk.rejected, (state, action) => {
				state.isLoadingDetail = false;
				state.detailError = action.payload;
			});
	},
});

export const { setRespondentFilters, setDateFilters, clearRespondentDetail } =
	surveyAnalyticsSlice.actions;

export default surveyAnalyticsSlice.reducer;
